import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export interface BulkDocumentExportCommandDto {
  trancheId: number | null
  loanIds: string[]
  documentTypeIds: number[]
  financingProgramId: string
  language: string
}

export interface BulkExportErrorCategoryCount {
  category: string
  count: number
}

export interface BulkExportTaskListItemDto {
  instanceId: string
  commandId: string
  requesterEmail: string
  financingProgramId: string
  runtimeStatus: string
  createdAt: string
  completedAt: string | null
  total: number
  staged: number
  errorCount: number
  zipUrl: string | null
  errorCountsByCategory: BulkExportErrorCategoryCount[]
}

const baseUrl = '/BulkDocumentExport'

const bulkDocumentExportKeys = {
  all: ['bulkDocumentExport'] as const,
  tasks: (mineOnly: boolean, days: number) => [...bulkDocumentExportKeys.all, 'tasks', mineOnly, days] as const,
}

async function startBulkDocumentExport(command: BulkDocumentExportCommandDto): Promise<void> {
  const apiClient = getApiClient()
  await apiClient.post(`${baseUrl}/Start`, command)
}

async function listBulkDocumentExportTasks(mineOnly: boolean, days: number): Promise<BulkExportTaskListItemDto[]> {
  const apiClient = getApiClient()
  const response = await apiClient.get<BulkExportTaskListItemDto[]>(`${baseUrl}/Tasks`, {
    params: { mineOnly, days },
  })
  return response.data
}

export function useStartBulkDocumentExport(): [
  MutationFunction<void, BulkDocumentExportCommandDto>,
  boolean,
] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: startBulkDocumentExport,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bulkDocumentExportKeys.all })
    },
  })

  return [mutateAsync, isPending]
}

export function useBulkDocumentExportTasks(
  mineOnly = true,
  days = 30,
): [BulkExportTaskListItemDto[], boolean, () => void] {
  const queryClient = useQueryClient()
  const { data, isFetching } = useQuery({
    queryKey: bulkDocumentExportKeys.tasks(mineOnly, days),
    queryFn: () => listBulkDocumentExportTasks(mineOnly, days),
    placeholderData: [],
  })
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: bulkDocumentExportKeys.tasks(mineOnly, days) })
  }
  return [data ?? [], isFetching, refresh]
}

/**
 * Fetches the per-task errors as a CSV (auth-required, so we go through apiClient instead of a
 * plain anchor href) and triggers a browser download.
 */
export async function downloadBulkDocumentExportTaskErrorsCsv(commandId: string): Promise<void> {
  const apiClient = getApiClient()
  const response = await apiClient.get<Blob>(
    `${baseUrl}/Tasks/${encodeURIComponent(commandId)}/Errors.csv`,
    { responseType: 'blob' },
  )

  const blob = new Blob([response.data], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', `bulk-export-errors-${commandId}.csv`)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}
